import { useEffect, useRef, useCallback } from "react";

export const useDebouncedApiCall = (delay = 800) => {
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  const execute = useCallback((callback) => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      // Abort previous in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new signal for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Run the provided callback and pass the signal to it
      callback(abortController.signal);
    }, delay);
  }, [delay]);

  const cancel = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (abortControllerRef.current) abortControllerRef.current.abort();
  }, []);

  return { execute, cancel };
};
